/** Períodos do financeiro: atalhos (mês atual, trimestre…) e navegação ◀ ▶. Datas ISO "AAAA-MM-DD". */

export type PresetKey =
  | "hoje" | "semana" | "mes" | "mes_anterior" | "proximo_mes" | "trimestre" | "trimestre_anterior"
  | "ano" | "ano_anterior" | "ultimos_30" | "ultimos_90" | "proximos_30" | "personalizado"
  /** Período alcançado pelas setas ◀ ▶. */
  | "outro";

export interface Period { preset: PresetKey; from: string; to: string }

export const PRESETS: Array<{ key: PresetKey; label: string }> = [
  { key: "hoje", label: "Hoje" },
  { key: "semana", label: "Esta semana" },
  { key: "mes", label: "Mês atual" },
  { key: "mes_anterior", label: "Mês anterior" },
  { key: "proximo_mes", label: "Próximo mês" },
  { key: "trimestre", label: "Trimestre atual" },
  { key: "trimestre_anterior", label: "Trimestre anterior" },
  { key: "ano", label: "Ano atual" },
  { key: "ano_anterior", label: "Ano anterior" },
  { key: "ultimos_30", label: "Últimos 30 dias" },
  { key: "ultimos_90", label: "Últimos 90 dias" },
  { key: "proximos_30", label: "Próximos 30 dias" },
  { key: "personalizado", label: "Personalizado" },
];

const pad = (n: number) => String(n).padStart(2, "0");
const iso = (y: number, m0: number, d: number) => { const x = new Date(Date.UTC(y, m0, d)); return `${x.getUTCFullYear()}-${pad(x.getUTCMonth() + 1)}-${pad(x.getUTCDate())}`; };
const parts = (s: string) => s.split("-").map(Number) as [number, number, number];
export const addDays = (s: string, n: number) => { const [y, m, d] = parts(s); return iso(y, m - 1, d + n); };
const monthRange = (y: number, m0: number, count = 1) => ({ from: iso(y, m0, 1), to: iso(y, m0 + count, 0) });
const daysBetween = (a: string, b: string) => Math.round((Date.UTC(...(([y, m, d]) => [y, m - 1, d] as const)(parts(b))) - Date.UTC(...(([y, m, d]) => [y, m - 1, d] as const)(parts(a)))) / 86_400_000);

export function presetRange(key: PresetKey, today: string): { from: string; to: string } {
  const [y, m, d] = parts(today);
  const m0 = m - 1;
  switch (key) {
    case "hoje": return { from: today, to: today };
    case "semana": {
      const dow = new Date(Date.UTC(y, m0, d)).getUTCDay(); // 0 = domingo
      const start = addDays(today, -((dow + 6) % 7)); // segunda
      return { from: start, to: addDays(start, 6) };
    }
    case "mes": return monthRange(y, m0);
    case "mes_anterior": return monthRange(y, m0 - 1);
    case "proximo_mes": return monthRange(y, m0 + 1);
    case "trimestre": return monthRange(y, Math.floor(m0 / 3) * 3, 3);
    case "trimestre_anterior": return monthRange(y, Math.floor(m0 / 3) * 3 - 3, 3);
    case "ano": return { from: iso(y, 0, 1), to: iso(y, 11, 31) };
    case "ano_anterior": return { from: iso(y - 1, 0, 1), to: iso(y - 1, 11, 31) };
    case "ultimos_30": return { from: addDays(today, -29), to: today };
    case "ultimos_90": return { from: addDays(today, -89), to: today };
    case "proximos_30": return { from: today, to: addDays(today, 29) };
    default: return monthRange(y, m0);
  }
}

export function makePeriod(key: PresetKey, today: string): Period {
  return { preset: key, ...presetRange(key, today) };
}

/** Anda para trás (-1) ou para frente (+1) mantendo o tamanho do período. */
export function shiftPeriod(p: Period, dir: -1 | 1): Period {
  const [fy, fm, fd] = parts(p.from);
  const [ty, tm, td] = parts(p.to);
  const lastOfTo = new Date(Date.UTC(ty, tm, 0)).getUTCDate();
  // Meses inteiros (mês, trimestre, ano): anda em meses.
  if (fd === 1 && td === lastOfTo) {
    const months = (ty - fy) * 12 + (tm - fm) + 1;
    const r = monthRange(fy, fm - 1 + dir * months, months);
    return { preset: p.preset === "personalizado" ? "personalizado" : "outro", ...r };
  }
  const len = daysBetween(p.from, p.to) + 1;
  return { preset: p.preset === "personalizado" ? "personalizado" : "outro", from: addDays(p.from, dir * len), to: addDays(p.to, dir * len) };
}

const MES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
/** Rótulo curto: "set/2026", "jul–set/2026", "01/09–15/09/2026". */
export function periodLabel(p: { from: string; to: string }): string {
  const [fy, fm, fd] = parts(p.from);
  const [ty, tm, td] = parts(p.to);
  const lastOfTo = new Date(Date.UTC(ty, tm, 0)).getUTCDate();
  if (fd === 1 && td === lastOfTo) {
    if (fy === ty && fm === tm) return `${MES[fm - 1]}/${fy}`;
    if (fy === ty && fm === 1 && tm === 12) return String(fy);
    return fy === ty ? `${MES[fm - 1]}–${MES[tm - 1]}/${ty}` : `${MES[fm - 1]}/${fy}–${MES[tm - 1]}/${ty}`;
  }
  if (p.from === p.to) return `${pad(fd)}/${pad(fm)}/${fy}`;
  return `${pad(fd)}/${pad(fm)}${fy !== ty ? `/${fy}` : ""}–${pad(td)}/${pad(tm)}/${ty}`;
}
